import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';
import FileTypeIcon, { getFileCategory, isPreviewable } from './FileTypeIcon';

const FilePreviewModal = ({ open, onClose, document: doc }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fullscreen, setFullscreen] = useState(false);

  if (!doc) return null;

  const fileName = doc.key ? doc.key.replace(/^tenants\/[^/]+\/uploads\//, '') : '';
  const fileCategory = getFileCategory(fileName);
  const canPreview = isPreviewable(fileName);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  };

  const handleDownload = () => {
    if (doc.url) {
      const link = document.createElement('a');
      link.href = doc.url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleOpenInNew = () => {
    if (doc.url) {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderPreview = () => {
    if (!canPreview) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
            bgcolor: 'grey.50',
            borderRadius: 2,
            p: 4
          }}
        >
          <FileTypeIcon fileName={fileName} size="xlarge" showBackground />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {fileName}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Preview not available for this file type.
            <br />
            Click download to view the file.
          </Typography>
        </Box>
      );
    }

    if (fileCategory === 'image') {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 300,
            maxHeight: fullscreen ? '80vh' : 500,
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: 'grey.100',
            position: 'relative'
          }}
        >
          {loading && (
            <CircularProgress
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }}
            />
          )}
          <img
            src={doc.url}
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 8
            }}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError('Failed to load image');
            }}
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
            onClick={() => setFullscreen(!fullscreen)}
          >
            <FullscreenIcon />
          </IconButton>
        </Box>
      );
    }

    if (fileCategory === 'document' && fileName.toLowerCase().endsWith('pdf')) {
      return (
        <Box
          sx={{
            minHeight: 400,
            maxHeight: fullscreen ? '80vh' : 600,
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <iframe
            src={`${doc.url}#toolbar=1`}
            width="100%"
            height="100%"
            style={{ border: 'none', borderRadius: 8 }}
            title={fileName}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          bgcolor: 'grey.50',
          borderRadius: 2,
          p: 4
        }}
      >
        <FileTypeIcon fileName={fileName} size="xlarge" showBackground />
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
          {fileName}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Preview not available for this file type.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={fullscreen ? false : "md"}
      fullWidth
      fullScreen={fullscreen}
      PaperProps={{
        sx: {
          minHeight: fullscreen ? '100vh' : 400
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
        <FileTypeIcon fileName={fileName} size="medium" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div" noWrap>
            {fileName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={fileCategory.charAt(0).toUpperCase() + fileCategory.slice(1)}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={formatFileSize(doc.size)}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} color="inherit">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          renderPreview()
        )}

        {/* File Metadata */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            File Information
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, fontSize: '0.875rem' }}>
            <Typography color="text.secondary">Last Modified:</Typography>
            <Typography>{formatDate(doc.lastModified)}</Typography>
            <Typography color="text.secondary">Size:</Typography>
            <Typography>{formatFileSize(doc.size)}</Typography>
            <Typography color="text.secondary">Type:</Typography>
            <Typography>{fileCategory.charAt(0).toUpperCase() + fileCategory.slice(1)}</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Tooltip title="Download file">
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            variant="contained"
            color="primary"
          >
            Download
          </Button>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <Button
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNew}
            variant="outlined"
          >
            Open in New Tab
          </Button>
        </Tooltip>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewModal;